import { FREE_USER_MESSAGE_SEND_LIMIT } from '@mm/constants/message-send-limits';
import { getPercentage } from '@mm/utils';
import { isNumber } from 'lodash';
import { createSelector } from 'reselect';
import { getPlanInfoWithFeatures } from '../helpers/billing-info-helpers';
import {
  BasicPage,
  EnhancedBillingPage,
  PageBillingType,
} from '../interfaces/connected-page';
import {
  Plan,
  PlansMapping,
  PlanSubtypes,
  planSubtypesByType,
  PlanTypeBE,
} from '../interfaces/subscription';
import ConnectedPageModel, {
  EnhancedOutgoingMessagesStatistic,
  OutgoingMessagesStatistic,
} from '../model/ConnectedPageModel';
import { StoreState } from '../store-interfaces';

export const selectAvailablePages = (state: StoreState) =>
  state.connectedPage.availablePages || { data: [] };

export const selectActiveFacebookPage = (state: StoreState) =>
  state.connectedPage.pageDetails;
export const selectBasicPages = (state: StoreState) =>
  state.connectedPage.basicPagesList;
export const selectAccessiblePagesByRoles = (state: StoreState) =>
  state.connectedPage.accessiblePagesByRoles;
export const selectPlans = (state: StoreState) => state.subscription.plans;
export const selectPlansMapping = (state: StoreState) =>
  state.subscription.plansMapping;
export const selectPlansPrices = (state: StoreState) =>
  state.subscription.plansPrices;

export const selectPlansAllotments = (state: StoreState) =>
  state.subscription.planAllotments;

export const selectAnnualPlansAllotments = (state: StoreState) =>
  state.subscription.annualPlanAllotments;

export const selectSpecialDeal = (state: StoreState) =>
  state.subscription.specialDeal;

export const selectActiveFacebookPageFromSession = (state: StoreState) => {
  const activeCompanyId =
    state.session?.session?.userAttributes.activeCompanyId;
  return activeCompanyId
    ? state.connectedPage.basicPagesList.find(
        p => p.companyId === activeCompanyId,
      )
    : undefined;
};

const getFacebookPages = (state: StoreState, props: any) => {
  const connectedPages = state.connectedPage.entities;
  const {
    match: {
      params: { pageId },
    },
  } = props;
  return connectedPages.find(page => page.id === Number(pageId));
};

const getFacebookPagesFromActiveBot = (state: StoreState) => {
  const chatbot = state.chatbot.activeChatbot;

  return chatbot && chatbot.connectedPages && chatbot.connectedPages.length > 0
    ? chatbot.connectedPages[0].name
    : 'Your FB Page Name';
};

export const getMessagesStatistic = (
  facebookPage?: ConnectedPageModel,
): EnhancedOutgoingMessagesStatistic => {
  const DEFAULT: OutgoingMessagesStatistic = {
    pageMessagingCount: 0,
    prevPageMessagingCount: 0,
    subscriptionMessagingCount: 0,
    prevSubscriptionMessagingCount: 0,
    pageLockedAt: '',
    limitReachedAt: null,
  };

  if (facebookPage) {
    const outgoingMessagesStatistic =
      facebookPage.outgoingMessageUsage || DEFAULT;

    const count = outgoingMessagesStatistic.subscriptionMessagingCount || 0;
    const limitReachedAt = outgoingMessagesStatistic.limitReachedAt || '';
    const pageLockedAt = outgoingMessagesStatistic.pageLockedAt || '';
    const outgoingMessageLimit = isNumber(facebookPage.outgoingMessagesLimit)
      ? facebookPage.outgoingMessagesLimit
      : Number.MAX_SAFE_INTEGER;
    const cappedCount = Math.min(count, outgoingMessageLimit);
    const utilizationPercentage = getPercentage(count, outgoingMessageLimit);
    const messagesRemaining = outgoingMessageLimit - cappedCount;
    const pageSpecificCount = outgoingMessagesStatistic.pageMessagingCount;

    return {
      count,
      limitReachedAt,
      pageLockedAt,
      outgoingMessageLimit,
      utilizationPercentage,
      cappedCount,
      messagesRemaining,
      pageSpecificCount,
    };
  }

  return {
    pageSpecificCount: 0,
    count: 0,
    limitReachedAt: '',
    pageLockedAt: '',
    utilizationPercentage: 0,
    outgoingMessageLimit: FREE_USER_MESSAGE_SEND_LIMIT,
    cappedCount: 0,
    messagesRemaining: Number.MAX_SAFE_INTEGER,
  };
};

export const getPlan = (facebookPage: ConnectedPageModel, plans: Plan[]) => {
  if (facebookPage.subscription) {
    const { planCode } = facebookPage.subscription;
    return plans.find(plan => plan.planCode === planCode);
  }
};

const getPlanCodeFromMapping = (
  planType: string,
  planCode: string,
  mapping: PlansMapping,
) => {
  if (mapping[planType] && Array.isArray(mapping[planType])) {
    return mapping[planType].includes(planCode);
  }
  return mapping[planType] === planCode;
};

export const getPageBillingTypeByPlan = (
  plan: Plan,
  mapping?: PlansMapping,
): PageBillingType => {
  if (plan.planType === PlanTypeBE.teamSpecial) {
    return PageBillingType.SpecialTeam;
  }
  if (plan.planType === PlanTypeBE.proSpecial) {
    if (mapping && getPlanCodeFromMapping('wp', plan.planCode, mapping)) {
      return PageBillingType.WP;
    }
    if (
      mapping &&
      getPlanCodeFromMapping('instachamp', plan.planCode, mapping)
    ) {
      return PageBillingType.Instachamp;
    }
    return PageBillingType.SpecialPro;
  }

  if (plan.planType === PlanTypeBE.team) {
    if (plan.legacy) {
      return PageBillingType.LegacyTeam;
    }
    if (
      plan.planSubtypes &&
      plan.planSubtypes.some(subType =>
        planSubtypesByType.team.includes(subType),
      )
    ) {
      return PageBillingType.Team;
    }
    return PageBillingType.CustomTeam;
  }

  if (
    plan.planSubtypes &&
    plan.planSubtypes.some(subType =>
      planSubtypesByType.unicorn.includes(subType),
    )
  ) {
    return PageBillingType.ProUnicorn;
  }
  if (
    plan.planSubtypes &&
    plan.planSubtypes.some(subType => planSubtypesByType.pro.includes(subType))
  ) {
    return PageBillingType.Pro;
  }
  if (plan.legacy) {
    return PageBillingType.LegacyPro;
  }

  return PageBillingType.CustomPro;
};

export const getPageBillingType = (
  page: ConnectedPageModel,
  mapping?: PlansMapping,
  plan?: Plan,
): PageBillingType => {
  if (plan) {
    const type = getPageBillingTypeByPlan(plan, mapping);

    const specialTypes = [
      PageBillingType.SpecialTeam,
      PageBillingType.SpecialPro,
      PageBillingType.WP,
      PageBillingType.Instachamp,
    ];

    if (plan.planSubtypes.some(subType => subType === PlanSubtypes.team2020)) {
      return PageBillingType.NewTeam;
    }
    // Promotion > Special plan
    if (specialTypes.indexOf(type) === -1 || !page.redemption) {
      return type;
    }
  }

  if (page.redemption) {
    if (page.redemption.teamTrial) {
      return PageBillingType.TrialTeam;
    }
    return PageBillingType.Promotion;
  }

  // this rare case happens when we fetch pages for another company but do not update plans
  if (page.subscriptions && page.subscriptions.length > 0) {
    if (
      page.subscriptions.find(
        sub => [PlanTypeBE.pro, PlanTypeBE.team].indexOf(sub.planType) >= 0,
      )
    ) {
      return PageBillingType.CustomPro;
    }
    return PageBillingType.SpecialPro;
  }

  return PageBillingType.Free;
};

export const getPageWithBillingInfo = (
  page: ConnectedPageModel | BasicPage | undefined,
  plans: Plan[],
  mapping?: PlansMapping,
): EnhancedBillingPage | undefined => {
  if (page) {
    const plan = getPlan(page, plans);
    const pageBillingType = getPageBillingType(page, mapping, plan);
    const messagesStatistic = getMessagesStatistic(page);

    return {
      ...page,
      messagesStatistic,
      pageBillingType,
      plan,
    };
  }
};

export const getEnhancedBillingPages = (
  pages: BasicPage[],
  plans: Plan[],
  mapping?: PlansMapping,
): EnhancedBillingPage[] => {
  return pages.reduce<EnhancedBillingPage[]>((enhancedPages, page) => {
    const enhancedPage = getPageWithBillingInfo(page, plans, mapping);

    if (enhancedPage) {
      return [...enhancedPages, enhancedPage];
    }

    return enhancedPages;
  }, []);
};

const selectFacebookPage = (facebookPage: ConnectedPageModel) => facebookPage;

export const facebookPageSelector = createSelector(
  [getFacebookPages],
  selectFacebookPage,
);

export const facebookFromActiveBotPageSelector = createSelector(
  [getFacebookPagesFromActiveBot],
  selectFacebookPage,
);

export const enhancedBillingPagesSelector = createSelector(
  [selectBasicPages, selectPlans, selectPlansMapping],
  getEnhancedBillingPages,
);

export const freePagesSelector = (state: StoreState) => {
  const source = state.connectedPage;
  const entities =
    source.entities.length > 0 ? source.entities : source.basicPagesList;
  return entities.filter(page => {
    return !page.subscription && !page.redemption;
  });
};

export const activeFacebookPageSelector = createSelector(
  [selectActiveFacebookPage, selectPlans, selectPlansMapping],
  getPageWithBillingInfo,
);

export const activeFacebookPagePlanInfoSelector = createSelector(
  [selectPlansMapping, activeFacebookPageSelector],
  getPlanInfoWithFeatures,
);

export const activeMessagesStatisticSelector = createSelector(
  [activeFacebookPageSelector],
  getMessagesStatistic,
);
